<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import { useModal } from '@/composition/use-modal';
  import {
    EDITOR_DAYS,
    HOURS_EDITOR_MODAL,
    addRange,
    cloneWeek,
    copyDayToOpenDays,
    copySourceDay,
    invalidHoursDays,
    longWeekdayNames,
    normalizeEditedWeek,
    rangeEndsNextDay,
    removeRange,
    setDayOpen,
    updateRange,
  } from '@/domain/store-setup/hours-editor';
  import type { IHoursEditorResult } from '@/domain/store-setup/hours-editor';
  import {
    MAX_RANGES_PER_DAY,
    defaultWeek,
  } from '@/domain/store-setup/opening-hours';
  import type {
    ITimeRange,
    TWeekHours,
  } from '@/domain/store-setup/opening-hours';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Horario de atención» (plan §4.2.1), a dynamic modal: Monday to Sunday,
   * each day open or closed with 1 to 3 ranges; a range ending at or before
   * its start ends the next day. The first open day offers «Usar este
   * horario todos los días». «Listo» resolves `{ week }`, «No cargar
   * horario» `{ week: null }`, «Cancelar» closes without a result.
   */
  const TIME_STEP_SECONDS = 900;

  const props = withDefaults(
    defineProps<{
      value?: TWeekHours | null;
    }>(),
    { value: null }
  );
  const emit = defineEmits<{ (e: 'close'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();

  const uid = nextDomId('setup-hours');
  const week = ref<TWeekHours>(cloneWeek(props.value ?? defaultWeek()));

  const locale = computed(() => proxy.$i18n.locale);
  const dayNames = computed(() => longWeekdayNames(locale.value));
  const invalidDays = computed(() => invalidHoursDays(week.value));
  const canSubmit = computed(() => invalidDays.value.length === 0);
  /** The first open day: it offers «Usar este horario todos los días». */
  const copySource = computed(() => copySourceDay(week.value));
  const rows = computed(() =>
    EDITOR_DAYS.map((day) => ({
      day,
      name: dayNames.value[day],
      hours: week.value[day],
      invalid: invalidDays.value.includes(day),
    }))
  );

  function dayLabelId(day: number): string {
    return `${uid}-day-${day}`;
  }

  function toggleDay(day: number, open: boolean) {
    week.value = setDayOpen(week.value, day, open);
  }

  function setTime(
    day: number,
    index: number,
    field: keyof ITimeRange,
    value: string
  ) {
    week.value = updateRange(week.value, day, index, field, value);
  }

  function addDayRange(day: number) {
    week.value = addRange(week.value, day);
  }

  function removeDayRange(day: number, index: number) {
    week.value = removeRange(week.value, day, index);
  }

  function copyToAll() {
    const source = copySource.value;
    if (source !== null) week.value = copyDayToOpenDays(week.value, source);
  }

  function submit(result: IHoursEditorResult) {
    void modals.dialogSubmit(HOURS_EDITOR_MODAL, result);
  }

  function done() {
    if (!canSubmit.value) return;
    submit({ week: normalizeEditedWeek(week.value) });
  }

  function clearHours() {
    submit({ week: null });
  }
</script>

<template>
  <div class="v2-store-hours-editor-modal">
    <header class="hours-header">
      <h2 class="hours-title">{{ $t('storeSetup.hours.title') }}</h2>
      <button
        type="button"
        class="hours-close"
        :aria-label="$t('storeSetup.hours.cancel')"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="20" />
      </button>
    </header>
    <div class="hours-body">
      <div
        v-for="row in rows"
        :key="row.day"
        class="day-row"
        role="group"
        :aria-labelledby="dayLabelId(row.day)"
      >
        <div class="day-head">
          <span :id="dayLabelId(row.day)" class="day-name">{{ row.name }}</span>
          <PosUiSwitch
            :value="row.hours.open"
            :label="$t('storeSetup.hours.open')"
            @input="toggleDay(row.day, $event)"
          />
        </div>
        <div v-if="row.hours.open" class="day-ranges">
          <div
            v-for="(range, index) in row.hours.ranges"
            :key="index"
            class="range"
          >
            <div class="range-inputs">
              <PosUiInput
                class="range-time"
                type="time"
                :step="TIME_STEP_SECONDS"
                :value="range.from"
                :aria-label="$t('storeSetup.hours.from')"
                @input="setTime(row.day, index, 'from', $event)"
              />
              <span class="range-separator" aria-hidden="true">–</span>
              <PosUiInput
                class="range-time"
                type="time"
                :step="TIME_STEP_SECONDS"
                :value="range.to"
                :aria-label="$t('storeSetup.hours.to')"
                @input="setTime(row.day, index, 'to', $event)"
              />
              <button
                type="button"
                class="range-remove"
                :aria-label="$t('storeSetup.hours.removeRange')"
                :title="$t('storeSetup.hours.removeRange')"
                @click="removeDayRange(row.day, index)"
              >
                <PosUiIcon name="close" :size="18" />
              </button>
            </div>
            <p v-if="rangeEndsNextDay(range)" class="range-note">
              {{ $t('storeSetup.hours.nextDay') }}
            </p>
          </div>
          <div class="day-actions">
            <button
              v-if="row.hours.ranges.length < MAX_RANGES_PER_DAY"
              type="button"
              class="day-action"
              @click="addDayRange(row.day)"
            >
              <PosUiIcon name="plus" class="action-icon" />
              {{ $t('storeSetup.hours.addRange') }}
            </button>
            <button
              v-if="row.day === copySource"
              type="button"
              class="day-action"
              @click="copyToAll"
            >
              <PosUiIcon name="content-copy" class="action-icon" />
              {{ $t('storeSetup.hours.copyToAll') }}
            </button>
          </div>
          <p v-if="row.invalid" class="day-error" role="alert">
            {{ $t('storeSetup.hours.invalid') }}
          </p>
        </div>
      </div>
    </div>
    <footer class="hours-footer">
      <PosUiButton
        class="footer-clear"
        color="neutral"
        variant="ghost"
        @click="clearHours"
        >{{ $t('storeSetup.hours.clear') }}</PosUiButton
      >
      <div class="footer-main">
        <PosUiButton color="neutral" variant="outline" @click="emit('close')">{{
          $t('storeSetup.hours.cancel')
        }}</PosUiButton>
        <PosUiButton :disabled="!canSubmit" @click="done">{{
          $t('storeSetup.hours.done')
        }}</PosUiButton>
      </div>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .v2-store-hours-editor-modal {
    @apply tw-flex tw-flex-col tw-bg-white;
    max-height: 85vh;
    .hours-header {
      @apply tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-py-3 tw-pl-5 tw-pr-3;
    }
    .hours-title {
      @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
    }
    .hours-close {
      @apply tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-bg-neutral-100 hover:tw-text-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .hours-body {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-px-5;
      overscroll-behavior: contain;
    }
    .day-row {
      @apply tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-py-3;
      &:last-child {
        @apply tw-border-b-0;
      }
    }
    .day-head {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3;
    }
    .day-name {
      @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .day-ranges {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .range {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .range-inputs {
      @apply tw-flex tw-items-center tw-gap-2;
    }
    .range-time {
      @apply tw-min-w-0 tw-flex-1;
      max-width: 9rem;
    }
    .range-separator {
      @apply tw-shrink-0 tw-text-neutral-400;
    }
    .range-remove {
      @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-bg-neutral-100 hover:tw-text-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .range-note {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .day-actions {
      @apply tw-flex tw-flex-wrap tw-gap-x-4;
    }
    .day-action {
      @apply tw-inline-flex tw-min-h-11 tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-px-0 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-700 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .action-icon {
      @apply tw-text-base;
    }
    .day-error {
      @apply tw-m-0 tw-text-xs tw-text-red-600;
    }
    .hours-footer {
      @apply tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3;
    }
    .footer-main {
      @apply tw-ml-auto tw-flex tw-gap-2;
    }
  }
</style>
